import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  AnimationPlaybackControls,
  MotionProps,
  animate,
  motion,
  useInView,
  useMotionValue,
  useScroll,
  useTransform,
} from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOnClickOutside } from 'usehooks-ts'
import useAnalytics from '~/src/hooks/useAnalytics'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import useFetchDisoverFeed, { Item } from '~/src/hooks/useFetchDisoverFeed'
import useGetExperimentActive from '~/src/hooks/useGetExperimentActive'
import { cn } from '~/src/lib/utils'

dayjs.extend(relativeTime)

const MAX_CHUNKS = 6

interface Props extends React.PropsWithChildren {
  className?: string
  wrapperClassName?: string
  forceAnimation?: boolean
  disableOverscroll?: boolean
  initialScaleMobile?: number
  finalScaleMobile?: number
  initialScaleDesktop?: number
  finalScaleDesktop?: number
}

const DupeDiscoverPreview: React.FC<Props> = ({
  children,
  className,
  wrapperClassName,
  disableOverscroll,
  forceAnimation,
  initialScaleMobile = 1,
  finalScaleMobile = 1,
  initialScaleDesktop = 1,
  finalScaleDesktop = 1,
}) => {
  const { isMobile } = useDeviceDetect()
  const [processedItems, setProcessedItems] = useState<Item[]>([])
  const [numChunks, setNumChunks] = useState(MAX_CHUNKS)
  const { data } = useFetchDisoverFeed({
    limit: 200,
    multiplier: 100,
    enabled: true,
  })
  const [allImagesProcessed, setAllImagesProcessed] = useState(false)
  const [chunkedData, setChunkedData] = useState<Item[][]>([])
  const { scrollYProgress } = useScroll()
  const ref = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, {
    once: false,
    amount: 0.4,
    margin: '150% 0px 0px 0px',
  })

  const initialScale = isMobile ? initialScaleMobile : initialScaleDesktop
  const finalScale = isMobile ? finalScaleMobile : finalScaleDesktop
  const scale = useTransform(
    scrollYProgress,
    [0, 0.25],
    [initialScale, finalScale]
  )

  const randomizedData = useMemo(() => {
    return (data || []).sort(() => Math.random() - 0.5)
  }, [data])

  const checkImageDimensions = useCallback(async (item: Item) => {
    if (!item.image) return null

    try {
      const img = new window.Image()
      const src = item.image
      const proxySrc = `https://assets.dupe.com/${encodeURIComponent(src)}?tr=w-400,c-at_max`

      const imageLoadPromise = new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = proxySrc
      })

      // Create a timeout promise so we are not waiting forever
      // on very large images
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          img.src = '' // Cancel the image loading
          reject(new Error('Image load timeout'))
        }, 2000)
      })

      // Race between image loading and timeout
      await Promise.race([imageLoadPromise, timeoutPromise])

      if (img.width >= 300 && img.height >= 300) {
        return {
          ...item,
          width: img.width,
          height: img.height,
        }
      }

      return null
    } catch (error) {
      console.error(`Failed to load image for item ${item.id}:`, error)
      return null
    }
  }, [])

  // Check dimensions for all images when data changes
  useEffect(() => {
    if (allImagesProcessed) return
    if (!randomizedData.length) return

    const promises = randomizedData.map((item) => checkImageDimensions(item))
    Promise.allSettled(promises).then((results) => {
      const fulfilledResults = results.filter(
        (result) => result.status === 'fulfilled'
      ) as PromiseFulfilledResult<Item | null>[]

      const processed = fulfilledResults
        .filter((result) => result && result.value !== null)
        .map((result) => result.value)
        .filter((item) => !!item)
        .slice(0, 100)

      setProcessedItems(processed)

      // All image checks have completed (successfully or not)
      setAllImagesProcessed(true)
    })
  }, [allImagesProcessed, checkImageDimensions, randomizedData])

  // Chunk the data
  useEffect(() => {
    const chunks = processedItems.reduce((acc, item, index) => {
      const itemsPerChunk = Math.ceil(processedItems.length / numChunks)
      const chunkIndex = Math.min(
        Math.floor(index / itemsPerChunk),
        numChunks - 1
      )
      acc[chunkIndex] = acc[chunkIndex] || []

      if (item) {
        acc[chunkIndex].push(item)
      }
      return acc
    }, [] as Item[][])

    setChunkedData(chunks)
  }, [numChunks, processedItems])

  // Set mobile or desktop chunking
  useEffect(() => {
    setNumChunks(isMobile ? 3 : MAX_CHUNKS)

    return () => {
      setNumChunks(MAX_CHUNKS)
    }
  }, [isMobile])

  return (
    <div ref={rootRef} className={cn('min-h-screen w-full', wrapperClassName)}>
      {children}

      <div
        ref={ref}
        className={cn(
          'transition-opacity delay-300 duration-500',
          'min-h-screen overflow-y-hidden',
          {
            'mt-[-100dvh] opacity-100': allImagesProcessed,
            'opacity-0': !allImagesProcessed,
            'max-h-[300dvh]': !disableOverscroll,
            'max-h-[100dvh]': disableOverscroll,
          },
          className
        )}
      >
        <motion.div
          className='relative flex w-full gap-2 md:px-2'
          style={{
            scale,
            transformOrigin: 'center center',
          }}
        >
          {chunkedData?.map((chunk, columnIndex) => (
            <div key={columnIndex} className='flex flex-1 flex-col'>
              <ColumnContainer
                isInView={(!isMobile && isInView) || forceAnimation}
                isMobile={isMobile}
              >
                {chunk.map((item) => (
                  <ColumnItem
                    key={item.id}
                    item={item}
                    columnIndex={columnIndex}
                  />
                ))}
              </ColumnContainer>
            </div>
          ))}
        </motion.div>
      </div>

      {/* <div className='pointer-events-none absolute inset-x-0 top-0 h-[100dvh] bg-gradient-to-b from-black/90 to-transparent' /> */}
    </div>
  )
}

interface ColumnItemProps {
  item: Item
  columnIndex?: number
}

const ColumnItem: React.FC<ColumnItemProps> = ({ item, columnIndex }) => {
  const analytics = useAnalytics()
  const [active, setActive] = useState(false)

  const randomTimestamp = useMemo(() => {
    const now = new Date().getTime()
    const sevenDaysAgo = now - 2 * 24 * 60 * 60 * 1000
    const timestamp = new Date(
      sevenDaysAgo + Math.random() * (now - sevenDaysAgo)
    )

    const t = dayjs(timestamp)
    return t.fromNow()
  }, [])

  const inputContainerRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(inputContainerRef as React.RefObject<HTMLElement>, () =>
    setActive(false)
  )

  const { active: showDupeAIHomepage } = useGetExperimentActive(
    FeatureFlags.DupeAIHomepage
  )

  return (
    <div
      ref={inputContainerRef}
      onClick={() => {
        setActive(!active)
      }}
      className={cn('group mb-2 block')}
    >
      <div className='relative cursor-pointer overflow-hidden'>
        <Image
          src={`https://assets.dupe.com/${encodeURIComponent(item.image)}?tr=w-300`}
          alt={item.image}
          width={item.width || 300}
          height={item.height || 300}
          unoptimized
          onContextMenu={(e) => e.preventDefault()}
          className='w-full rounded object-cover md:rounded-lg'
          style={{
            aspectRatio: `${item.width} / ${item.height}`,
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
          draggable={false}
        />
        <div
          className={cn(
            'absolute inset-0 bg-black/90 opacity-80 transition-opacity md:group-hover:opacity-40',
            {
              '!opacity-10': active,
            }
          )}
          onContextMenu={(e) => e.preventDefault()}
        />

        <Link
          href={
            showDupeAIHomepage
              ? `/chats/${item.hash}?ref=home-discover`
              : `/results/${item.hash}?ref=home-discover`
          }
          target='_blank'
          rel='noreferrer noopener'
          className={cn(
            'absolute inset-x-0 bottom-0 flex items-center justify-center rounded-b bg-black/80 p-2 text-sm text-white transition-all md:justify-between md:rounded-b-lg',
            'translate-y-full md:group-hover:translate-y-0',
            {
              'translate-y-0': active,
            }
          )}
          onClick={() => {
            if (active) {
              analytics.track(
                AnalyticsEventName.DupeDiscoverPreviewClick,
                {
                  ...item,
                },
                {
                  sendToPlausible: true,
                  sendToFb: true,
                }
              )
            }
          }}
        >
          <div
            className={cn('hidden text-xs md:block', {
              'md:pl-[var(--sidebar-width-collapsed)]': columnIndex === 0,
            })}
          >
            Found {randomTimestamp.toLocaleString()}
          </div>
          <button className='w-full rounded border border-white/25 bg-black px-2.5 py-1.5 text-xs font-medium md:w-auto'>
            See Similar
          </button>
        </Link>
      </div>
    </div>
  )
}

const ColumnContainer: React.FC<
  MotionProps & {
    children: React.ReactNode
    isInView?: boolean
    isMobile?: boolean
  }
> = ({ children, isInView, isMobile, ...props }) => {
  const y = useMotionValue('0%')
  const [isHovered, setIsHovered] = useState(false)
  const animationRef = useRef<AnimationPlaybackControls>(undefined)

  useEffect(() => {
    // Don't start animation if not in view
    if (!isInView) {
      animationRef.current?.stop()
      return
    }

    // const normalDuration = Math.random() * (250 - 200) + 200
    const normalDuration = Math.random() * (400 - 350) + 350
    const slowDuration = normalDuration * 2

    const animation = animate(y, '-100%', {
      type: 'tween',
      ease: 'linear',
      duration: isHovered ? slowDuration : normalDuration,
      repeat: Infinity,
      onStop: () => {
        const currentY = y.get()
        y.setWithVelocity('0%', currentY, y.getVelocity())
      },
    })

    animationRef.current = animation

    return () => {
      animation.stop()
    }
  }, [isHovered, y, isInView])

  return (
    <motion.div
      {...props}
      style={{
        y,
        transform: 'translate3d(0, 0, 0)',
        transformStyle: 'preserve-3d',
        backfaceVisibility: 'hidden',
        willChange: 'transform',
      }}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      className='relative'
    >
      <div className='w-full'>{children}</div>
      <div className='absolute left-0 top-full w-full'>{children}</div>
    </motion.div>
  )
}

export default DupeDiscoverPreview
